import ChecklistFilter from './ChecklistFilter';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import ChecklistSwitchChange from '../ChecklistSwitchChange';
import { checklistTableStyles } from '../../../../utils/dataTableStyles';

interface Checklist {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    total_questions: number;
    max_possible_score: number;
    total_responses: number;
    created_at: string;
    updated_at: string;
}

interface ChecklistFilters {
    page: number;
    per_page: number;
    sort: string;
    order: string;
    search: string;
}

interface Props {
    checklists: Checklist[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: ChecklistFilters;
    setFilter: (filter: ChecklistFilters) => void;
    paginationRowsPerPageOptions?: any[];
    callbackSuccess: () => void;
    onSearch: () => void;
}

const ChecklistDataTable = ({
    checklists,
    totalRows,
    loading = false,
    edit,
    destroy,
    filter,
    setFilter,
    callbackSuccess,
    onSearch
}: Props) => {
    const columns = [
        {
            name: 'Nombre',
            selector: (row: Checklist) => row.name,
            sortable: true,
            sortField: 'name',
            minWidth: '320px',
            grow: 2,
            cell: (row: Checklist) => (
                <div
                    className="py-1 pe-3"
                    style={{
                        minWidth: 0,
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'normal',
                        lineHeight: 1.55
                    }}
                >
                    <strong
                        className="d-block mb-2"
                        style={{ fontSize: '0.95rem', lineHeight: 1.45 }}
                    >
                        {row.name}
                    </strong>
                    <p
                        className="mb-0 text-muted"
                        style={{
                            fontSize: '0.875rem',
                            lineHeight: 1.65,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            whiteSpace: 'normal'
                        }}
                    >
                        {row.description}
                    </p>
                </div>
            )
        },
        {
            name: 'Preguntas',
            selector: (row: any) => row.total_questions,
            sortable: true,
            sortField: 'total_questions',
            grow: 0,
            minWidth: '120px',
            cell: (row: Checklist) => (
                <div className="d-flex align-items-center py-1">
                    <span className="badge bg-info px-3 py-2">
                        {row.total_questions} preguntas
                    </span>
                </div>
            )
        },
        {
            name: 'Puntaje Máx.',
            selector: (row: any) => row.max_possible_score,
            sortable: true,
            sortField: 'max_possible_score',
            grow: 0,
            minWidth: '110px',
            cell: (row: Checklist) => (
                <div className="d-flex align-items-center py-1">
                    <span className="badge bg-primary px-3 py-2">
                        {row.max_possible_score} pts
                    </span>
                </div>
            )
        },
        {
            name: 'Respuestas',
            selector: (row: any) => row.total_responses,
            sortable: true,
            sortField: 'total_responses',
            grow: 0,
            minWidth: '120px',
            cell: (row: Checklist) => (
                <div className="d-flex align-items-center py-1">
                    <span className="badge bg-secondary px-3 py-2">
                        {row.total_responses} respuestas
                    </span>
                </div>
            )
        },
        {
            name: 'Activo',
            selector: (row: any) => row.is_active,
            sortable: true,
            sortField: 'is_active',
            grow: 0,
            minWidth: '100px',
            cell: (row: Checklist) => (
                <div className="d-flex align-items-center py-1">
                    <ChecklistSwitchChange
                        checkValue={row.is_active ?? true}
                        checklistId={row.id}
                        callbackSuccess={callbackSuccess}
                    />
                </div>
            )
        },
        {
            name: 'Acciones',
            selector: (row: any) => row.id,
            sortable: false,
            grow: 0,
            minWidth: '110px',
            cell: (row: Checklist) => (
                <div className="d-flex align-items-center py-1">
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row.id)}
                            title="Editar"
                            classIcon="fa-edit"
                            colorIcon="text-warning"
                            sizeIcon="16"
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row.id)}
                            title="Eliminar"
                            classIcon="fa-trash"
                            colorIcon="text-danger"
                            sizeIcon="16"
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row mt-3 mb-4">
                <div className="col">
                    <ChecklistFilter filter={filter} setFilter={setFilter} onSearch={onSearch} />
                </div>
            </div>

            <DefaultDataTable
                columns={columns}
                data={checklists}
                progressPending={loading}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
                styleTable={checklistTableStyles}
            />
        </>
    );
};

export default ChecklistDataTable;
